const C = { cream: "#FDFBF7", terra: "#C85A32", gold: "#D9A05B", sage: "#6E8576", sageD: "#536B5B", ink: "#2C221E", berry: "#B24E2A" };
const A = "M5 30 C 13 8, 20 8, 24 24 S 35 40, 43 18";
const B = "M5 18 C 13 40, 20 40, 24 24 S 35 8, 43 30";
const S = 1080;

export const FAMILY_NAME_BAD = /[<>]|https?:|www\.|\b[a-z0-9-]+\.(com|net|org|io|au|co|app|me|info|biz|xyz|ly)\b/i;
export const familyNameError = (name) => {
  if (name.length > 30) return "Please keep your family name to 30 characters or fewer.";
  if (FAMILY_NAME_BAD.test(name)) return "Please use plain text only — no links or HTML.";
  return "";
};
export const cardHeadline = (name) => (name ? `${name} are joining Woven` : "We're joining Woven");

const stroke = (ctx, d, color, width) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke(new Path2D(d));
};

const logo = (ctx, x, y, scale) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.lineCap = "round";
  stroke(ctx, B, C.gold, 4.5);
  stroke(ctx, A, C.cream, 8.5);
  stroke(ctx, A, C.terra, 4.5);
  ctx.beginPath();
  ctx.rect(18, 12, 12, 24);
  ctx.clip();
  ctx.lineCap = "butt";
  stroke(ctx, B, C.cream, 8.5);
  stroke(ctx, B, C.gold, 4.5);
  ctx.restore();
};

const wavePath = (y, sign, x0, x1) => {
  const p = new Path2D();
  for (let x = x0; x <= x1; x += 3) p[x === x0 ? "moveTo" : "lineTo"](x, y + sign * 16 * Math.sin((x - x0) / 50));
  return p;
};

const ribbon = (ctx, y) => {
  const [x0, x1] = [130, 950];
  ctx.save();
  ctx.lineCap = "round";
  const gold = wavePath(y, -1, x0, x1);
  const terra = wavePath(y, 1, x0, x1);
  [[gold, C.gold, 7], [terra, C.cream, 15], [terra, C.terra, 7]].forEach(([p, c, w]) => { ctx.strokeStyle = c; ctx.lineWidth = w; ctx.stroke(p); });
  for (let k = 1; k * Math.PI * 50 < x1 - x0; k += 2) {
    const xc = x0 + k * Math.PI * 50;
    ctx.save();
    ctx.beginPath();
    ctx.rect(xc - 20, y - 30, 40, 60);
    ctx.clip();
    [[C.cream, 15], [C.gold, 7]].forEach(([c, w]) => { ctx.strokeStyle = c; ctx.lineWidth = w; ctx.stroke(gold); });
    ctx.restore();
  }
  ctx.restore();
};

const leaf = (ctx, angle, len, color) => {
  ctx.save();
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(len * 0.25, -len * 0.42, len * 0.75, -len * 0.42, len, 0);
  ctx.bezierCurveTo(len * 0.75, len * 0.42, len * 0.25, len * 0.42, 0, 0);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = "rgba(253,251,247,0.45)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(6, 0);
  ctx.lineTo(len - 8, 0);
  ctx.stroke();
  ctx.restore();
};

const holly = (ctx, x, y, rot) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  leaf(ctx, -0.35, 96, C.sage);
  leaf(ctx, -1.25, 84, C.sageD);
  [[0, 0], [20, 10], [-4, 22]].forEach(([bx, by]) => {
    ctx.fillStyle = C.berry;
    ctx.beginPath();
    ctx.arc(bx, by, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(255,230,220,0.8)";
    ctx.beginPath();
    ctx.arc(bx - 4, by - 5, 3.5, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
};

const star = (ctx, x, y, r) => {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.quadraticCurveTo(x, y, x, y + r);
  ctx.quadraticCurveTo(x, y, x - r, y);
  ctx.quadraticCurveTo(x, y, x, y - r);
  ctx.fillStyle = C.gold;
  ctx.fill();
};

const wrap = (ctx, text, maxW) => {
  const lines = [];
  let line = "";
  text.split(" ").forEach((word) => {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = word; } else line = test;
  });
  return [...lines, line];
};

const loadFonts = () => Promise.all([
  document.fonts.load('400 92px "Fraunces"'),
  document.fonts.load('italic 400 34px "Fraunces"'),
  document.fonts.load('600 40px "Plus Jakarta Sans"'),
]).catch(() => {});

export async function drawCard(canvas, headline) {
  await loadFonts();
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = C.cream;
  ctx.fillRect(0, 0, S, S);
  const g = ctx.createRadialGradient(S / 2, 430, 40, S / 2, 430, 600);
  g.addColorStop(0, "rgba(242,201,143,0.38)");
  g.addColorStop(1, "rgba(242,201,143,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = "rgba(217,160,91,0.55)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(46, 46, S - 92, S - 92, 56);
  ctx.stroke();

  ribbon(ctx, 128);
  ribbon(ctx, 952);
  holly(ctx, 124, 128, 0);
  holly(ctx, 956, 952, Math.PI);
  [[230, 300, 16], [860, 262, 22], [160, 640, 12], [920, 660, 14], [300, 770, 10], [790, 770, 12]].forEach(([x, y, r]) => star(ctx, x, y, r));

  logo(ctx, S / 2 - 72, 196, 3);
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = C.ink;
  ctx.font = '400 52px "Fraunces", Georgia, serif';
  ctx.fillText("Woven", S / 2, 410);

  let size = 92;
  ctx.font = `400 ${size}px "Fraunces", Georgia, serif`;
  let lines = wrap(ctx, headline, 860);
  if (lines.length > 2) {
    size = 76;
    ctx.font = `400 ${size}px "Fraunces", Georgia, serif`;
    lines = wrap(ctx, headline, 880);
  }
  const lh = size * 1.12;
  const top = 560 - ((lines.length - 1) * lh) / 2;
  lines.forEach((l, i) => ctx.fillText(l, S / 2, top + i * lh));

  ctx.fillStyle = C.terra;
  ctx.font = '600 40px "Plus Jakarta Sans", sans-serif';
  ctx.fillText("Arriving Christmas Eve 2026", S / 2, top + (lines.length - 1) * lh + 100);
  ctx.fillStyle = C.sageD;
  ctx.font = 'italic 400 34px "Fraunces", Georgia, serif';
  ctx.fillText("One thread is a start. Two threads is a life worth living.", S / 2, 860);
}
